import _, { find, get, includes, map, reduce, replace, round, startsWith } from 'lodash';
import {
  cancellationCouponSource,
  couponDiscountTypeMap,
  couponSourceTypeMap,
  couponTypes,
  marketingCampaignPageQueries,
} from '@/views/config/coupon/helper/constant';
import { getOnlinePlanName, getPlanSelectedNumbers } from '@/common/product';
import { BILLING_CYCLE } from '@/common/constants';
import { getNextPstCycleDate } from '@/utils/dateformat';
import { getDecimalsWithCurrency, NEED_REMOVE_DECIMAL_CURRENCY } from '@/utils/money';
import { isEmptyVal } from '@/utils';
import TermsConditionDialog from '@/views/config/coupon/dialog/terms-condition-dialog';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import { PLAN_PRICE_CHANGE_UTILS } from '@/common/priceChange';
import { getEffectiveCurrency } from '@/utils/currency';

/**
 * Get default coupon state object
 * @returns {Object} Default coupon state
 */
export const getDefaultCouponState = () => {
  return {
    couponCode: null,
    couponType: couponTypes.NO_DISCOUNT,
    couponSourceType: null,
    timeBase: [],
    account_discount: 0,
    errorMessage: null,
    allowChange: true,
    discountInfo: {},
    discountInfoExtension: {},
    fromCampaignLink: false, // May come from verify coupon API if couponCategory is mt
    fromPreloadLink: false, // To distinguish which coupons are from campaign link
    errorCode: null,
    tncMessage: null,
    subscriptionLevelAction: null,
  };
};

/**
 * Handle coupon error message by merging with default state
 * @param {Object} couponResult - Coupon result object
 * @returns {Object} Merged coupon state
 */
export const handleCouponErrMsg = (couponResult = {}) => {
  return { ...getDefaultCouponState(), ...couponResult };
};

/**
 * Get default parameters for coupon processing
 * @returns {Object} Default parameters object
 */
const getDefaultParams = () => {
  return {
    selectedPlanList: [],
    planWithAmountMap: {},
    discountPlanList: [],
  };
};

/**
 * Utility class for threshold coupon calculations
 */
export class ThresholdCouponUtil {
  /**
   * Get sorted discount list from threshold coupon
   * @param {ThresHoldCouponResponse} thresholdCoupon - Threshold coupon response
   * @returns {Array<number>} Sorted array of threshold values
   */
  getDiscountList = (thresholdCoupon = {}) => {
    const { thresholdList = [] } = thresholdCoupon;
    return map(thresholdList, (item) => item?.thresholdValue)
      .sort((a, b) => a - b);
  };
  /**
   * Calculate total amount for threshold coupon
   * @param {ThresHoldCouponResponse} thresholdCoupon - Threshold coupon response
   * @param {Object} getters - Vuex getters
   * @returns {Object} Total amount and discount plan map
   */
  getTotalAmount = (thresholdCoupon = {}, getters) => {
    const supportPlanCodeList = thresholdCoupon.supportPlanCodeList;
    const getSelectedPlansMap = getters.getEffectSelectedPlansMap;

    return reduce(Object.keys(getSelectedPlansMap), (result, planCode) => {
      const curSelectedPlan = get(getSelectedPlansMap, planCode);
      const realPlanCode = get(curSelectedPlan, "plan_code", "");

      if (includes(supportPlanCodeList, realPlanCode)) {
        const plan = getSelectedPlansMap[planCode];
        const singleTotal = (plan.amount / 100) * plan.cartInfo.selected_number;
        result.total += singleTotal;
        result.discountPlanMap[planCode] = singleTotal;
      }

      return result;
    }, {
      total: 0,
      discountPlanMap: {},
    });
  };

  /**
   * Get threshold item by discount value
   * @param {Array} thresholdList - List of threshold items
   * @param {number} newDiscount - Discount value to find
   * @returns {Object} Threshold item or empty object
   */
  getThresholdItem(thresholdList = [], newDiscount = 0) {
    return find(thresholdList, (item) => item.thresholdValue === newDiscount) || {};
  }

  /**
   * Calculate discount amount for threshold coupon
   * @param {number} totalAmount - Total amount
   * @param {ThresHoldCouponResponse} thresholdCoupon - Threshold coupon response
   * @returns {Object} Discount calculation result
   */
  getDiscountAmount = (totalAmount = 0, thresholdCoupon = {}) => {
    let newDiscount = 0;
    let newCurTierAmount = 0;
    const { thresholdList = [] } = thresholdCoupon;
    const discountList = this.getDiscountList(thresholdCoupon);

    // Find the highest applicable discount threshold
    discountList.forEach((discount) => {
      if (totalAmount >= discount) {
        newDiscount = discount;
      }
    });

    if (newDiscount) {
      const thresholdItem = this.getThresholdItem(thresholdList, newDiscount);
      newDiscount = thresholdItem?.discountValue || 0;
      newCurTierAmount = thresholdItem?.thresholdValue || 0;
    }

    const index = discountList?.indexOf(newCurTierAmount);
    const nextIndex = index > -1 ? index + 1 : 0;

    return {
      nextThresholdItem: this.getThresholdItem(thresholdList, get(discountList, `[${nextIndex}]`, null)),
      actualDiscountAmount: newDiscount,
      actualTierAmount: newCurTierAmount,
    };
  };
  /**
   * Calculate discount for threshold coupon
   * @param {ThresHoldCouponResponse} thresholdCoupon - Threshold coupon response
   * @param {Object} getters - Vuex getters
   * @returns {ThresHoldResponse} Threshold response with discount calculations
   */
  calculateDisCount = (thresholdCoupon = {}, getters) => {
    const { total = 0, discountPlanMap } = this.getTotalAmount(thresholdCoupon, getters);
    const { nextThresholdItem, actualDiscountAmount, actualTierAmount } = this.getDiscountAmount(total, thresholdCoupon);
    const getSelectedPlansMap = getters.getEffectSelectedPlansMap;

    // Calculate discount for each supported plan
    const thresholdDiscountPlans = reduce(Object.keys(getSelectedPlansMap), (result, planCode) => {
      const curSelectedPlan = get(getSelectedPlansMap, planCode);
      const realPlanCode = get(curSelectedPlan, "plan_code", "");

      if (includes(thresholdCoupon?.supportPlanCodeList, realPlanCode)) {
        const singleAmount = discountPlanMap[planCode];
        const singlePercentage = round(singleAmount / total, 10);
        const discountAmount = singlePercentage * actualDiscountAmount;

        result[planCode] = {
          ...getSelectedPlansMap[planCode],
          discountAmount,
        };
      }

      return result;
    }, {});

    return {
      thresholdDiscountPlans,
      actualDiscountAmount,
      actualTierAmount,
      totalAmount: total,
      nextTierAmount: nextThresholdItem?.thresholdValue || 0,
      nextDiscountAmount: nextThresholdItem?.discountValue || 0,
      gapTotalAmount: (nextThresholdItem?.thresholdValue || 0) - total,
    };
  };
  /**
   * Replace placeholders in terms and conditions message
   * @param {string} tnc - Terms and conditions message
   * @param {Object} params - Parameters for replacement
   * @returns {string} Processed terms and conditions message
   */
  replaceTncInfo = (tnc = "", params = {}) => {
    const { nextInvoiceDate, amountWithoutDiscount, productNameList, totalDiscount } = params;

    if (tnc && totalDiscount) {
      tnc = replace(tnc, /\${next_invoice_date}/g, nextInvoiceDate);
      tnc = replace(tnc, /\${product_name_list}/g, productNameList);
      return replace(tnc, /\${amount_without_discount}/g, amountWithoutDiscount);
    }

    return "";
  };

  /**
   * Process a single plan's threshold discount
   * @param {string} planCode - Plan code
   * @param {Object} plan - Plan object with discount information
   * @param {Object} expireDates - Expire dates mapping
   * @returns {Object|null} Processed plan discount or null if invalid
   */
  static processPlan(planCode, plan, expireDates) {
    const discountAmountInCents = plan?.discountAmount * DISCOUNT_AMOUNT_MULTIPLIER;

    // Skip if discount amount is effectively zero
    if (validRoundValueIsZero(discountAmountInCents)) {
      return null;
    }

    const billingCycle = plan.billing_cycle;
    const discountField = BILLING_CYCLE_DISCOUNT_MAP[billingCycle];
    const expireDateField = BILLING_CYCLE_EXPIRE_DATE_MAP[billingCycle];

    // Skip if billing cycle is not supported
    if (!discountField || !expireDateField) {
      return null;
    }

    return {
      planCode,
      discountAmount: discountAmountInCents,
      discountField,
      expireDate: expireDates[expireDateField],
      billingCycle,
    };
  }

  /**
   * Aggregate threshold discount results using reduce
   * @param {Array} processedPlans - Array of processed plan discounts
   * @returns {CouponDiscountMapResultBean} Aggregated discount result
   */
  static aggregate(processedPlans = []) {
    return _.reduce(processedPlans, (result, { planCode, discountAmount, discountField, expireDate }) => {
      // Add to appropriate billing cycle discount
      result[discountField] += discountAmount;
      result.totalDiscount += discountAmount;

      // Store discount details for the plan
      result.discountPlans[planCode] = {
        expireDate,
        discountAmount,
        couponPeriod: COUPON_PERIOD_DEFAULT,
        couponDefType: couponSourceTypeMap.threshold,
        discountType: couponDiscountTypeMap.thresholdAmount,
      };

      return result;
    }, getInitialDiscountResult());
  }
}

export const thresholdCouponUtil = new ThresholdCouponUtil();

// Constants for threshold coupon processing
const DISCOUNT_AMOUNT_MULTIPLIER = 100; // Convert to cents
const COUPON_PERIOD_DEFAULT = 1;

// Billing cycle to discount field mapping
const BILLING_CYCLE_DISCOUNT_MAP = {
  [BILLING_CYCLE.ANNUAL]: 'annualDiscount',
  [BILLING_CYCLE.MONTHLY]: 'monthlyDiscount',
};

// Billing cycle to expire date field mapping
const BILLING_CYCLE_EXPIRE_DATE_MAP = {
  [BILLING_CYCLE.ANNUAL]: 'nextAnnual',
  [BILLING_CYCLE.MONTHLY]: 'nextMonthly',
};

/**
 * Calculate discount amount based on discount type
 * @param {Object} options - Discount calculation options
 * @param {number} options.discountType - Type of discount (percentage or fixed amount)
 * @param {number} options.discount - Discount value
 * @param {number} options.amount - Base amount
 * @param {number} options.quantity - Quantity
 * @returns {number} Calculated discount amount
 */
export const calculateDiscountWithDiscountType = ({
  discountType = couponDiscountTypeMap.percentage,
  discount = 0,
  amount = 0,
  quantity = 1,
}) => {
  switch (discountType) {
    case couponDiscountTypeMap.fixedAmount:
      return Number(discount) * Number(quantity);
    case couponDiscountTypeMap.percentage:
    default:
      return (discount / 100) * amount;
  }
};

/**
 * Calculate discount for a specific plan
 * @param {Object} options - Discount options
 * @param {string} options.planCode - Plan code
 * @param {number} options.discountType - Discount type
 * @param {number} options.discount - Discount value
 * @param {Array} selectedPlanList - List of selected plans
 * @param {Object} planWithAmountMap - Plan amount mapping
 * @returns {Object} Plan and calculated discount amount
 */
const calculatePerDiscount = (options = {}, selectedPlanList = [], planWithAmountMap = {}) => {
  const { planCode, discountType, discount } = options;

  const plan = find(selectedPlanList, p => p.plan_code === planCode) || {};
  const discountAmount = calculateDiscountWithDiscountType({
    discountType,
    discount,
    quantity: getPlanSelectedNumbers(plan),
    amount: get(planWithAmountMap, [planCode], 0),
  });

  // Round to 2 decimal places and convert back to cents
  const roundDiscountAmount = _.round(discountAmount / 100, 2) * 100;

  return {
    plan,
    discountAmount: roundDiscountAmount,
  };
};

export const getInitialDiscountResult = () => {
  return {
    annualDiscount: 0,
    monthlyDiscount: 0,
    quarterlyDiscount: 0,
    twoYearsDiscount: 0,
    threeYearsDiscount: 0,
    annualTermMonthlyDiscount: 0,
    totalDiscount: 0,
    discountPlans: {},
    thresholdResult: {},
  };
}

/**
 * Process coupon discount information for each plan in the shopping cart
 * @param {Object} params - Parameters including discountPlanList, selectedPlanList, planWithAmountMap
 * @returns {CouponDiscountMapResultBean} Aggregated discount result for all plans
 */
export const processCoupon = (params = {}) => {
  // Merge default params with incoming params
  const {
    discountPlanList,
    selectedPlanList,
    planWithAmountMap
  } = { ...getDefaultParams(), ...params };

  // Initialize result object for discount aggregation
  const initialResult = getInitialDiscountResult();

  // Iterate over each discount plan in the list
  return _.reduce(discountPlanList, (result, item) => {
    const { planCode, ...rest } = item;

    // Only process if the plan exists in the cart and has a discount
    if (planWithAmountMap[planCode] && rest?.discount) {
      // Calculate the discount amount for the current plan
      const { plan, discountAmount } = calculatePerDiscount(item, selectedPlanList, planWithAmountMap);

      // Aggregate discount by billing cycle type
      switch (plan.billing_cycle) {
        case BILLING_CYCLE.ANNUAL:
          result.annualDiscount += discountAmount;
          break;
        case BILLING_CYCLE.MONTHLY:
          // Exclude one month plans
          if (!plan.isOneMonth) {
            result.monthlyDiscount += discountAmount;
          }
          break;
        case BILLING_CYCLE.QUARTERLY:
          result.quarterlyDiscount += discountAmount;
          break;
        case BILLING_CYCLE.TWO_YEARS:
          result.twoYearsDiscount += discountAmount;
          break;
        case BILLING_CYCLE.THREE_YEARS:
          result.threeYearsDiscount += discountAmount;
          break;
        case BILLING_CYCLE.ANNUAL_TERM_MONTHLY:
          result.annualTermMonthlyDiscount += discountAmount;
          break;
        default:
          // No action for other billing cycles
          break;
      }

      // Add to total discount
      result.totalDiscount += discountAmount;

      // Store discount details for the current plan
      result.discountPlans[planCode] = {
        ...rest,
        discountAmount,
      };
    }

    return result;
  }, initialResult);
};

/**
 * Calculate threshold coupon discount result
 * @param {Object} couponState - Coupon state object
 * @param {Object} getters - Vuex getters
 * @returns {CouponDiscountMapResultBean} Threshold coupon discount result
 */
export const generateThresholdCoupon = (couponState, getters) => {
  try {
    // Calculate threshold discount using utility
    const thresholdResult = thresholdCouponUtil.calculateDisCount(couponState, getters);
    const thresholdDiscountPlans = thresholdResult.thresholdDiscountPlans || {};

    // Get expire dates for different billing cycles
    const expireDates = getNextPstCycleDate();

    // Process each plan with threshold discount
    const processedPlans = Object.entries(thresholdDiscountPlans)
      .map(([planCode, plan]) => ThresholdCouponUtil.processPlan(planCode, plan, expireDates))
      .filter(Boolean); // Remove null values

    // Aggregate the results
    const aggregatedResult = ThresholdCouponUtil.aggregate(processedPlans);

    return {
      ...aggregatedResult,
      thresholdResult,
    };
  } catch (error) {
    console.error('Error calculating threshold coupon discount:', error);
    // Return initial result with error information
    return {
      ...getInitialDiscountResult(),
      thresholdResult: { error: error.message },
    };
  }
};

/**
 * Check if rounded value equals zero
 * @param {number} amount - Amount to check
 * @param {Object} options - Options object
 * @param {number} options.divisionNumber - Division number for rounding
 * @returns {boolean} Whether the rounded value equals zero
 */
export const validRoundValueIsZero = (amount, options = {
  divisionNumber: 100,
}) => {
  if (isNaN(+amount)) return false;
  const decimals = getDecimalsWithCurrency(amount);
  return round(amount / options.divisionNumber, decimals) === 0;
};

/**
 * Get plan names grouped by billing cycle for threshold discount plans
 * @param {Object} thresholdDiscountPlans - Threshold discount plans object
 * @returns {Object} Plan names grouped by billing cycle
 */
export const getPlanNamesWithBillingCycle = (thresholdDiscountPlans = {}) => {
  return _.chain(thresholdDiscountPlans)
  .keys()
  .reduce((result, planCode) => {
    const plan = get(thresholdDiscountPlans, [planCode], {});
    const isOneMonth = get(plan, "isOneMonth", false);
    const { billing_cycle: billingCycle } = plan;
    let categoryName = getOnlinePlanName(plan);
    categoryName = categoryName ? `<p class="threshold-coupon-product-name">${categoryName}</p>` : "";

    if ((billingCycle === BILLING_CYCLE.MONTHLY || billingCycle === BILLING_CYCLE.PAY_AS_YOU_GO) && !isOneMonth) {
      result.monthly.push(categoryName);
    } else if (billingCycle === BILLING_CYCLE.ANNUAL) {
      result.annual.push(categoryName);
    }

    return result;
  }, {
    monthly: [],
    annual: [],
  })
  .mapValues((value) => _.uniq(value))
  .value();
};

/**
 * Show coupon terms and conditions message dialog
 * @param {string} tncMessage - Terms and conditions message
 * @returns {Promise} Promise that resolves when dialog is closed
 */
export const showCouponTermsMessage = async (tncMessage = "", getters = {}) => {
  if (tncMessage) {
    const couponSourceType = _.get(getters, ["couponState", "couponSourceType"], "");
    const couponTrackPrefix = couponSourceType === couponSourceTypeMap.threshold ? 'threshold coupon' : 'coupon';

    window.$tracking?.buyInteraction?.(
      "checkout options",
      `${couponTrackPrefix} modal - impression`
    )
    const dialog = new ServiceConfirm(TermsConditionDialog);
    return dialog.confirm({ termsCondition: tncMessage })
      .then(() => {
        window.$tracking?.buyInteraction?.(
          "checkout options",
          `${couponTrackPrefix} modal - accept`
        )
        return Promise.resolve();
      })
      .catch(() => {
        window.$tracking?.buyInteraction?.(
          "checkout options",
          `${couponTrackPrefix} modal - reject`
        )
        return Promise.reject();
      });
  }

  return Promise.resolve();
};

/**
 * Check if current URL is a short URL with coupon code
 * @param {Object} query - Query parameters object
 * @returns {boolean} Whether it's a short URL with coupon code
 */
export const isShortUrlWithCouponCode = (query = {}) => {
  return startsWith(location.pathname, '/z_url/') && isEmptyVal(query?.coupon) === false;
};

/**
 * Check if current request is from cancel campaign
 * @returns {boolean} Whether it's from cancel campaign
 */
export const isFromCancelCampaign = () => {
  return window?.$config("cp_source") === cancellationCouponSource;
};

/**
 * Add pricing mapping code field to list items
 * @param {Array} list - List of items
 * @param {string} key - Key to get plan code from
 * @returns {Array} List with added priceMappingPlanCode field
 */
export const addPricingMappingCodeField = (list = [], key = 'planCode') => {
  return _.reduce(list, (accumulator, item) => {
    const planCode = _.get(item, key);
    const priceMappingPlanCode = PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(planCode) || "";

    return [...accumulator, {
      ...item,
      priceMappingPlanCode,
    }];
  }, []);
};

/**
 * Get valid amount in minor currency
 * @param {number} amount - Amount to convert
 * @returns {number} Valid amount in minor currency
 */
export const getValidAmountInMinorCurrency = (amount) => {
  return NEED_REMOVE_DECIMAL_CURRENCY.includes(getEffectiveCurrency()) ? Math.round(amount * 100) : amount;
};

/**
 * Get valid amount for HPM charge
 * @param {number} amount - Amount to convert
 * @returns {string|number} Valid amount for HPM charge
 */
export const getValidAmountForHpmCharge = (amount) => {
  return NEED_REMOVE_DECIMAL_CURRENCY.includes(getEffectiveCurrency()) ? (amount / 100).toFixed(2) : amount;
};

/**
 * Check if current request is from campaign link
 * @returns {boolean} Whether it's from campaign link
 */
export const isFromCampaignLink = () => {
  return isEmptyVal(window.$config?.('marketingCoupon')) === false;
};

/**
 * Get persist coupon query information
 * @returns {Object} Persist coupon query info
 */
export const getPersistCouponQueryInfo = () => {
  const queryCouponCode = window.$config('coupon') || window.$config('persistCoupon');

  if (queryCouponCode) {
    return {
      persistCoupon: queryCouponCode,
    };
  }

  return {};
};

/**
 * Check if current request is from marketing campaign (contains cartId/marketing coupon/partner referral)
 * @returns {boolean} Whether it's from marketing campaign
 */
export const isFromMarketingCampaign = (filterKeys = []) => {
  let list = marketingCampaignPageQueries;

  if (filterKeys.length) {
    list = _.filter(marketingCampaignPageQueries, (p) => !_.includes(filterKeys, p))
  }

  return _.some(list, (key) => window.$config(key));
};

export const filterNotSupportPlanCodesForThresholdCoupon = (supportPlanCodeList = []) => {
  let notSupportPlanCodes = [];

  // for annual term monthly plan, not support threshold coupon
  if (!window.campaignFlags?.enableThresholdCouponForAnnualTermMonthly) {
    notSupportPlanCodes.push('zm_workplace_pro_annual_term_monthly_prepay');
  }

  return _.filter(supportPlanCodeList, (planCode) => !_.includes(notSupportPlanCodes, planCode));
};
